import i18next, { TFunction } from 'i18next';
import translation from '../locales/en/translation.json';

export type Translation = typeof translation;

type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

export type TranslationKeys = NestedKeyOf<Translation>;

type DynamicTFunction = TFunction & { keyPrefix?: string };
type StringResolver = (key: string) => string;

type JSONValue = string | number | boolean | JSONObject;
type JSONObject = { readonly [key: string]: JSONValue };

const translationKeys = new Set<string>();

const populateKeys = (source: JSONObject, prefix: string): void => {
  for (const key in source) {
    const path = prefix ? prefix + '.' + key : key;
    translationKeys.add(path);

    const value = source[key];
    if (typeof value === 'object') {
      populateKeys(value, path);
    }
  }
};

populateKeys(translation, '');

const isTranslationKey = (key: string): key is TranslationKeys => translationKeys.has(key);

const resolversByT = new WeakMap<DynamicTFunction, StringResolver>();

const translate = (t: DynamicTFunction, key: string, params?: Record<string, string | number>): string => {
  const absoluteKey = t.keyPrefix ? t.keyPrefix + '.' + key : key;
  if (isTranslationKey(absoluteKey)) return String(i18next.t(absoluteKey, params));
  if (params?.defaultValue != null) return String(params.defaultValue);
  return key;
};

const createResolver = (t: DynamicTFunction): StringResolver => {
  const cache: Record<string, string> = Object.create(null);
  return (key: string): string => cache[key] ?? (cache[key] = translate(t, key));
};

const resolverFor = (t: DynamicTFunction): StringResolver => {
  let resolver = resolversByT.get(t);
  if (!resolver) {
    resolver = createResolver(t);
    resolversByT.set(t, resolver);
  }
  return resolver;
};

export const translateDynamicKey = (
  t: DynamicTFunction,
  key: string,
  params?: Record<string, string | number>,
): string => (params ? translate(t, key, params) : resolverFor(t)(key));

export const translateWithParams = <Key extends TranslationKeys>(
  t: TFunction,
  key: Key,
  params: Record<string, string | number>,
): string | string[] => {
  const result = t(key, params);
  return typeof result === 'string' || Array.isArray(result) ? result : String(result);
};
